import { IconTable } from '@components/table/IconTable';
import { useTheme } from 'context/ThemeProvider/ThemeProvider';
import { CardContainer } from 'layout/Card';
import { type FC, useState } from 'react';
import useProjectsNeedAttentionColumns from './useGetBuildingNeedAttentionColumns';
import clsx from 'clsx';
import { useListProjectsBuildingView } from 'api/projects/projectsServices';
import { type ProjectData } from 'interfaces/projects/ProjectData.interface';
import { useNavigate } from 'react-router-dom';
import { useSeverity } from 'hooks/contexts/SeverityContext';

interface BuildingNeedAttentionTableProps {
  buildingId?: string;
  severity?: string;
}

const ProjectsNeedAttentionTable: FC<BuildingNeedAttentionTableProps> = ({
  buildingId,
  severity,
}) => {
  const [hoveredId, setHoveredId] = useState<undefined | number>(undefined);
  const { columns } = useProjectsNeedAttentionColumns();
  const { data, isFetching } = useListProjectsBuildingView(buildingId);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { getSeverityBorderColor, getSeveritySmileyIcon } = useSeverity();

  const navigateToProject = (project: ProjectData): void => {
    if (!project) return;
    const status =
      (project as any).project_status ||
      (project.baseline as any)?.project_status;

    if (status?.toLowerCase() === 'suggested') {
      const connectionPointId = project?.connection_point_id_client;
      if (connectionPointId) {
        navigate(`/app/management/energy_audit/${connectionPointId}`);
        return;
      }
    }
    navigate(`/app/management/projects/details/${project.id}`);
  };

  return (
    <div
      className={clsx(
        'rounded-lg',
        isFetching ? 'border border-gray-10' : getSeverityBorderColor(severity)
      )}
    >
      <CardContainer>
        <div className="flex items-center py-2 justify-between">
          {!isFetching && getSeveritySmileyIcon(severity)}
        </div>
        <IconTable
          minHeight={300}
          maxHeight={300}
          skeletonAmountWhenLoading={5}
          isFetching={isFetching}
          enableRowBorder={false}
          stripped={false}
          onRowHover={(id) => {
            setHoveredId(id);
          }}
          onClickRow={navigateToProject}
          hoveredId={hoveredId}
          hoverColor={theme.colors['tertiary-lighter']}
          enableRowRoundedBorder={true}
          data={data?.needs_attention ?? []}
          columns={columns}
          emptyTableMessage="No underperforming projects"
        />
      </CardContainer>
    </div>
  );
};
export default ProjectsNeedAttentionTable;
